import React from 'react';

import { useAppDispatch, useAppSelector } from '@/store';
import { SessionStatus, sessionSave } from '@/store/slices/session';

/**
 * Checkout email state, mirroring v2's `spark/stripe-payment` flow: local state
 * for the input, debounced-persisted (500ms) into the session's
 * `configuration_data.emailAddress` so the backend uses the buyer's edited
 * address for the invoice / post-payment mail (not the stale session value).
 *
 * Returns `[email, setEmail]` — `setEmail(value)` updates the input and schedules
 * the save. A late-arriving `initial` (e.g. the auth email hydrating after first
 * render) is adopted only while the field is still empty, so it never clobbers a
 * value the user is typing.
 */
export function useCheckoutEmail(initial: string): [string, (value: string) => void] {
  const dispatch = useAppDispatch();
  const session = useAppSelector((s) => s.session);
  // Latest session, read inside the debounced save so it never persists stale data.
  const sessionRef = React.useRef(session);
  sessionRef.current = session;

  const [email, setEmailLocal] = React.useState(initial);
  const timer = React.useRef<number | null>(null);

  React.useEffect(() => {
    if (initial) setEmailLocal((cur) => cur || initial);
  }, [initial]);

  React.useEffect(
    () => () => {
      if (timer.current != null) window.clearTimeout(timer.current);
    },
    [],
  );

  const setEmail = React.useCallback(
    (value: string): void => {
      setEmailLocal(value);
      if (timer.current != null) window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => {
        timer.current = null;
        const s = sessionRef.current;
        if (!s.sessionId || !s.loaded) return;
        // The payment acknowledge advances the session out of `configuration`, after which
        // `/session/save` rejects a `configuration_data` body outright (INVALID_SESSION_STATE).
        // This save sits on a 500ms debounce, so without the guard the buyer's last keystroke
        // before paying fires just past that boundary and 500s.
        if (s.status != null && s.status !== SessionStatus.configuration) return;
        dispatch(
          sessionSave({
            id: s.sessionId,
            data: {
              ...s.data,
              configuration_data: { ...s.data.configuration_data, emailAddress: value },
            },
          }),
        );
      }, 500);
    },
    [dispatch],
  );

  return [email, setEmail];
}
